import React from 'react';

interface DermoscopyTreatmentProps {
  dermoscopy: {
    performed: boolean;
    findings: string;
    pattern: string;
    ABCD_rule_score: number | null;
    melanoma_suspected: boolean;
    biopsy_recommended: boolean;
  };
  treatment: {
    topical: string;
    systemic: string;
    phototherapy: boolean;
    cryotherapy: boolean;
    laser: boolean;
    surgical_excision: boolean;
    follow_up_weeks: number | null;
    observations: string;
  };
  onFieldChange: (section: string, field: string, value: any) => void;
}

const PATTERN_OPTIONS = [
  { value: '', label: 'Seleccionar...' },
  { value: 'reticular', label: 'Reticular' },
  { value: 'globular', label: 'Globular' },
  { value: 'homogéneo', label: 'Homogéneo' },
  { value: 'estrellado', label: 'Estrellado' },
  { value: 'parallel pattern', label: 'Parallel Pattern' },
  { value: 'sin patrón definido', label: 'Sin Patrón Definido' },
];

const DermoscopyTreatment: React.FC<DermoscopyTreatmentProps> = ({ dermoscopy, treatment, onFieldChange }) => {
  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-search-plus me-1"></i>
            Dermatoscopía
          </h5>
          <div className="row mt-3">
            <div className="col-md-12">
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="dermoscopy_performed"
                    checked={dermoscopy.performed}
                    onChange={e => onFieldChange('dermoscopy', 'performed', e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="dermoscopy_performed">
                    Se realizó dermatoscopía
                  </label>
                </div>
              </div>
            </div>
          </div>

          {dermoscopy.performed && (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Patrón Dermatoscópico</label>
                    <select
                      className="form-select"
                      value={dermoscopy.pattern}
                      onChange={e => onFieldChange('dermoscopy', 'pattern', e.target.value)}
                    >
                      {PATTERN_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Puntaje ABCD</label>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      max={10}
                      step={0.1}
                      value={dermoscopy.ABCD_rule_score ?? ''}
                      onChange={e => onFieldChange('dermoscopy', 'ABCD_rule_score', e.target.value ? Number(e.target.value) : null)}
                      placeholder="0-10"
                    />
                  </div>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="mb-3">
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="melanoma_suspected"
                          checked={dermoscopy.melanoma_suspected}
                          onChange={e => onFieldChange('dermoscopy', 'melanoma_suspected', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="melanoma_suspected">
                          Sospecha Melanoma
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="biopsy_recommended"
                          checked={dermoscopy.biopsy_recommended}
                          onChange={e => onFieldChange('dermoscopy', 'biopsy_recommended', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="biopsy_recommended">
                          Biopsia Recomendada
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label className="form-label">Hallazgos Dermatoscópicos</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={dermoscopy.findings}
                      onChange={e => onFieldChange('dermoscopy', 'findings', e.target.value)}
                      placeholder="Describa hallazgos dermatoscópicos..."
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-medkit me-1"></i>
            Plan de Tratamiento
          </h5>
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Tratamiento Tópico</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={treatment.topical}
                  onChange={e => onFieldChange('treatment_derma', 'topical', e.target.value)}
                  placeholder="Corticoides tópicos, antifúngicos, retinoides, etc..."
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Tratamiento Sistémico</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={treatment.systemic}
                  onChange={e => onFieldChange('treatment_derma', 'systemic', e.target.value)}
                  placeholder="Antihistamínicos, corticoides orales, inmunosupresores, etc..."
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Procedimientos</label>
                <div className="d-flex flex-wrap gap-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="phototherapy"
                      checked={treatment.phototherapy}
                      onChange={e => onFieldChange('treatment_derma', 'phototherapy', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="phototherapy">Fototerapia</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="cryotherapy"
                      checked={treatment.cryotherapy}
                      onChange={e => onFieldChange('treatment_derma', 'cryotherapy', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="cryotherapy">Crioterapia</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="laser"
                      checked={treatment.laser}
                      onChange={e => onFieldChange('treatment_derma', 'laser', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="laser">Láser</label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="surgical_excision"
                      checked={treatment.surgical_excision}
                      onChange={e => onFieldChange('treatment_derma', 'surgical_excision', e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="surgical_excision">Escisión Quirúrgica</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label">Seguimiento (semanas)</label>
                <input
                  type="number"
                  className="form-control"
                  min={0}
                  value={treatment.follow_up_weeks ?? ''}
                  onChange={e => onFieldChange('treatment_derma', 'follow_up_weeks', e.target.value ? Number(e.target.value) : null)}
                  placeholder="Semanas"
                />
              </div>
            </div>
            <div className="col-md-8">
              <div className="mb-3">
                <label className="form-label">Observaciones</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={treatment.observations}
                  onChange={e => onFieldChange('treatment_derma', 'observations', e.target.value)}
                  placeholder="Notas adicionales del tratamiento..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DermoscopyTreatment;
